import React from 'react';
import {
  Activity,
  User,
  XCircle,
  Database,
  Filter,
  Search,
  RefreshCw,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { getActionBadgeColor, formatDate } from './securityHelpers';

const getStatusIcon = (status) => {
  if (status === 'success') return <CheckCircle className="h-4 w-4 text-green-500" />;
  if (status === 'failure') return <XCircle className="h-4 w-4 text-red-500" />;
  return <Clock className="h-4 w-4 text-gray-500" />;
};

const SummaryCard = ({ label, value, Icon, iconColor, valueClass = '' }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
        </div>
        <Icon className={`h-8 w-8 ${iconColor}`} />
      </div>
    </CardContent>
  </Card>
);

export const AuditLogsTab = ({
  auditSummary,
  failedActionsTotal,
  auditFilters,
  setAuditFilters,
  availableActions,
  availableResources,
  auditLogs,
  auditTotal,
  auditLoading,
  onApplyFilters,
}) => (
  <div className="space-y-4">
    {/* Audit Summary */}
    {auditSummary && (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard label="Total Events (7 days)" value={auditSummary.total_events} Icon={Activity} iconColor="text-blue-500" />
        <SummaryCard label="Login Events" value={auditSummary.by_action?.LOGIN || 0} Icon={User} iconColor="text-green-500" />
        <SummaryCard label="Failed Actions" value={failedActionsTotal} Icon={XCircle} iconColor="text-red-500" valueClass="text-red-600" />
        <SummaryCard label="Backup Events" value={auditSummary.by_action?.BACKUP_CREATED || 0} Icon={Database} iconColor="text-amber-500" />
      </div>
    )}

    {/* Filters */}
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Filter className="h-5 w-5" />
          Filters
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <Label>Action</Label>
            <Select
              value={auditFilters.action}
              onValueChange={(value) => setAuditFilters({ ...auditFilters, action: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {availableActions.map((action) => (
                  <SelectItem key={action} value={action}>{action}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Resource Type</Label>
            <Select
              value={auditFilters.resource_type}
              onValueChange={(value) => setAuditFilters({ ...auditFilters, resource_type: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Resources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                {availableResources.map((resource) => (
                  <SelectItem key={resource} value={resource}>{resource}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>User Email</Label>
            <Input
              placeholder="Search by email..."
              value={auditFilters.user_email}
              onChange={(e) => setAuditFilters({ ...auditFilters, user_email: e.target.value })}
            />
          </div>
          <div>
            <Label>Status</Label>
            <Select
              value={auditFilters.status}
              onValueChange={(value) => setAuditFilters({ ...auditFilters, status: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failure">Failure</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={onApplyFilters} className="w-full" data-testid="apply-filters-btn">
              <Search className="h-4 w-4 mr-2" />
              Apply Filters
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Audit Log Table */}
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Audit Log ({auditTotal} total)</CardTitle>
        <Button variant="outline" size="sm" onClick={onApplyFilters}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {auditLoading ? (
          <div className="text-center py-8">Loading audit logs...</div>
        ) : auditLogs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No audit logs found matching your filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm whitespace-nowrap">{formatDate(log.timestamp)}</TableCell>
                    <TableCell>
                      <Badge className={getActionBadgeColor(log.action)}>{log.action}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{log.resource_type}</span>
                      {log.resource_id && (
                        <span className="text-xs text-gray-500 block font-mono">
                          {log.resource_id.substring(0, 8)}...
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{log.user_email || 'System'}</span>
                      {log.user_role && (
                        <span className="text-xs text-gray-500 block">{log.user_role}</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <span className="text-xs text-gray-600 truncate block">
                        {JSON.stringify(log.details).substring(0, 50)}
                        {JSON.stringify(log.details).length > 50 && '...'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {getStatusIcon(log.status)}
                        <span className="text-sm">{log.status}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);
